/**
 * Inline Gleap launcher.
 *
 * Screens with their own bottom action bar (the wizard) mount
 * useInlineGleapLauncher(): while any is mounted, GleapChat hides Gleap's
 * floating bubble (it covered the Next button on phones) and the screen shows
 * its own chat button instead. `available` is true only when GleapChat would
 * have shown the bubble here — so the admin's enable flag, excluded paths and
 * partner-portal rule still decide whether chat exists at all.
 */

import { useEffect, useSyncExternalStore } from 'react';

let inlineRequests = 0;
let available = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** GleapChat: read whether a screen wants the launcher inline. */
export function useInlineGleapRequested(): boolean {
  return useSyncExternalStore(subscribe, () => inlineRequests > 0);
}

/** GleapChat: publish whether chat is available on the current route. */
export function setInlineGleapAvailable(next: boolean): void {
  if (available === next) return;
  available = next;
  emit();
}

/** A screen with its own chat button. */
export function useInlineGleapLauncher(): { available: boolean; open: () => void } {
  useEffect(() => {
    inlineRequests++;
    emit();
    return () => { inlineRequests--; emit(); };
  }, []);
  const isAvailable = useSyncExternalStore(subscribe, () => available);
  return { available: isAvailable, open: () => window.Gleap?.open() };
}
